export interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
}

/**
 * A minimal in-memory sliding-window rate limiter, keyed by an arbitrary
 * string. Sliding rather than fixed-window so a burst right at a window
 * boundary can't double a key's effective limit. Mirrors
 * `@reno-estimator/email`'s `RateLimiter` (same shape, independently
 * duplicated rather than imported, since `leads` and `email` are
 * deliberately kept independent of each other) - a shared extraction is
 * a reasonable follow-up if a third package ever needs the same logic.
 *
 * In-memory only, same disclosed limitation as `InMemoryLeadStore` -
 * fine for a single-process host or tests, not for a multi-instance
 * deployment (which needs a shared store, e.g. Redis). See
 * docs/privacy-security.md.
 */
export class RateLimiter {
  private readonly hits = new Map<string, number[]>();

  constructor(
    private readonly config: RateLimitConfig,
    private readonly now: () => number = () => Date.now()
  ) {}

  /** Returns true and records the attempt if the key is under its limit; returns false (and does not record) if the key is currently rate-limited. */
  tryConsume(key: string): boolean {
    const now = this.now();
    const windowStart = now - this.config.windowMs;
    const recent = (this.hits.get(key) ?? []).filter((timestamp) => timestamp > windowStart);

    if (recent.length >= this.config.maxRequests) {
      this.hits.set(key, recent);
      return false;
    }

    recent.push(now);
    this.hits.set(key, recent);
    return true;
  }
}
